import { memo, useCallback, useMemo } from 'react';
import { useAppSelector } from 'hooks/redux';
import SupportChatLayout from 'modules/chatbot/layouts/SupportChatLayout/SupportChatLayout';
import SSEErrorBoundary from 'modules/chatbot/components/ErrorBoundary/SSEErrorBoundary';
import NewChatOverlay from 'modules/chatbot/components/NewChatOverlay';
import PreparingChatOverlay from 'modules/chatbot/components/PreparingChatOverlay/PreparingChatOverlay';
import { ChatContainerProps } from 'modules/chatbot/types/chatbot.types';
import {
  selectActiveSessionMessages,
  selectIsFullScreen,
  WELCOME_MESSAGE_ID,
} from 'modules/chatbot/slices/chatbotSlice';
import { MESSAGE_LIMIT } from 'modules/chatbot/constants/constants';
import MessageList from 'modules/chatbot/components/MessageList/MessageList';
import styles from './chatBotContainer.module.less';

interface ChatBotContainerProps
  extends Omit<ChatContainerProps, 'onVisibilityChange'> {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  isFullScreen?: boolean;
}

const ChatBotContainer = memo<ChatBotContainerProps>(
  ({ visible, setVisible }: ChatBotContainerProps) => {
    const messages = useAppSelector(selectActiveSessionMessages);
    const isFullScreen = useAppSelector(selectIsFullScreen);

    // Filter messages to show only last MESSAGE_LIMIT messages + welcome message
    const displayMessages = useMemo(() => {
      // Separate welcome message from other messages
      const welcomeMessage = messages.find(
        (msg) => msg.id === WELCOME_MESSAGE_ID
      );
      const nonWelcomeMessages = messages.filter(
        (msg) => msg.id !== WELCOME_MESSAGE_ID
      );

      // Take last MESSAGE_LIMIT individual messages
      const limitedMessages = nonWelcomeMessages.slice(-MESSAGE_LIMIT);

      // Combine: [welcome message] + [last MESSAGE_LIMIT messages]
      return welcomeMessage
        ? [welcomeMessage, ...limitedMessages]
        : limitedMessages;
    }, [messages]);

    const handleClose = useCallback(() => {
      setVisible(false);
    }, [setVisible]);

    const containerClassName = isFullScreen
      ? `${styles.container} ${styles.containerFullScreen}`
      : styles.container;

    return (
      <div className={containerClassName}>
        <SSEErrorBoundary>
          <div className={styles.wrapperContainer}>
            <NewChatOverlay />
            <PreparingChatOverlay />
            <SupportChatLayout
              handleModalClose={handleClose}
              visible={visible}
              aria-label="Ask SAGE"
              role="dialog"
              aria-modal={true}
            >
              <MessageList
                messages={displayMessages}
                className={styles.messageList}
                virtualized
              />
            </SupportChatLayout>
          </div>
        </SSEErrorBoundary>
      </div>
    );
  }
);

ChatBotContainer.displayName = 'ChatBotContainer';

export default ChatBotContainer;
